import type { ReactElement } from "react"
import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import type { AxiosError } from "axios"

import { RequestsLineGraph } from "../../../components/graphs/Line/RequestsLineGraph"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { baseErrorType } from "../../../types/custom/projects"
import type { PaginatedListModelStateSwitchList } from "../../../types/generated/api/PaginatedListModelStateSwitchList"
import type { RequestSummaryDailyOutput } from "../../../types/generated/api/RequestSummaryDailyOutput"
import {
  convertActivityDataToMarkers,
  convertDatatoTotalRequestsLineGraph,
  fillMissingRequestSummaryDates,
} from "../../../utils/deploymentDetailsHelpers"
import type { RequestsPerDayGraphProps } from "../interfaces"

/**
 * Container component for handling requests line graph visualization
 * Manages data fetching and processing for model request statistics
 *
 * @param {Props} props - Component properties
 * @param {moment.Moment} startDate - Start date for data range
 * @param {moment.Moment} endDate - End date for data range
 * @returns {React.ReactElement}
 */
export function RequestsPerDayGraph(props: Readonly<RequestsPerDayGraphProps>): ReactElement {
  const { startDate, endDate } = props

  const { id: projectId } = useParams<{ id: string }>()

  const currentModel = useSelector((state: RootState) => state.models.currentModel)
  const { t } = useTranslation("models")

  // Query for fetching model activity history
  // Includes project-level state changes and deployments
  const { isLoading: isLoadingActivity, data: activityResponse } = useQuery<
    PaginatedListModelStateSwitchList,
    AxiosError<baseErrorType>
  >(["modelHistory", projectId], () => KonanAPI.getModelHistory(currentModel?.uuid as string), {
    enabled: projectId !== "",
  })

  // Query for fetching daily request summary data
  // Includes request counts and performance metrics
  const { isLoading: isLoadingDailyRequestSummary, data: dailyRequestSummaryResponse } =
    useQuery<RequestSummaryDailyOutput>(
      ["model-daily-request-summary", currentModel, startDate, endDate],
      () =>
        KonanAPI.fetchDailyModelRequestSummary({
          start_date: startDate?.toISOString() as string,
          end_date: endDate?.toISOString() as string,
          model_uuid: currentModel?.uuid,
        }),
      { enabled: !!startDate && !!endDate },
    )

  //add model name to activity response
  const activityData = useMemo(() => {
    if (activityResponse?.results && activityResponse?.results.length > 0) {
      return convertActivityDataToMarkers(currentModel?.name as string, activityResponse.results)
    }
    return []
  }, [activityResponse?.results, currentModel?.name])

  // Processes request data for line graph display
  // missing dates and converts to required format
  const adjustedData = useMemo(() => {
    if (startDate && endDate) {
      // If there's new data, fill in the missing dates and convert it to Serie[] type
      return convertDatatoTotalRequestsLineGraph(
        fillMissingRequestSummaryDates(startDate, endDate, dailyRequestSummaryResponse?.data ?? [], activityData),
      )
    }
    return []
  }, [startDate, endDate, dailyRequestSummaryResponse, activityData])

  return (
    <RequestsLineGraph
      title={t("graphs.requests_per_day.title")}
      data={adjustedData}
      graphHeight={340}
      isLoading={isLoadingDailyRequestSummary || isLoadingActivity}
    />
  )
}
